import React from 'react'
import { motion } from 'framer-motion'
import { Zap, Palette, Download, Shield, Apple } from 'lucide-react'
import { RiAndroidLine } from 'react-icons/ri'

const Features = () => {
  const features = [
    {
      icon: Apple,
      title: 'iOS Ready',
      description: 'Generates all iOS app icon sizes including App Store, iPhone, iPad, and more.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      icon: RiAndroidLine,
      title: 'Android Compatible',
      description: 'Generates adaptive Android icons for all density buckets from ldpi to xxxhdpi.',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100'
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Generate all required icon sizes in seconds with our optimized processing engine.',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    },
    {
      icon: Palette,
      title: 'Easy Customization',
      description: 'Adjust background colors, padding, and preview your icons in real-time.',
      color: 'text-pink-600',
      bgColor: 'bg-pink-100'
    },
    {
      icon: Download,
      title: 'Multiple Formats',
      description: 'Download individual sizes or get everything organized in a ZIP file.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      icon: Shield,
      title: 'Privacy First',
      description: 'All processing happens in your browser. Your images never leave your device.',
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    }
  ]

  return (
    <section id="features" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Why Choose EasyAppIcon?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to create professional app icons, 
            designed with developers in mind
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="group p-6 bg-white rounded-xl border border-gray-100 shadow-soft hover:shadow-hover transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${feature.bgColor} mb-4 group-hover:scale-110 transition-transform duration-200`}>
                <feature.icon className={`w-6 h-6 ${feature.color}`} />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {feature.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {[
              { number: '18', label: 'Icon Sizes', suffix: '+' },
              { number: '100', label: 'Free to Use', suffix: '%' },
              { number: '2', label: 'Platforms' },
              { number: '0', label: 'Data Collected' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  {stat.number}{stat.suffix}
                </div>
                <div className="text-sm text-gray-600">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Features 